import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Table,
  message,
  Row,
  Col,
  Space,
  DatePicker,
  Modal,
  Upload,
  InputNumber,
  Typography,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import API from "../../api";
import { useNavigate } from "react-router-dom";
import Barcode from "react-barcode";
import { useSelector } from "react-redux";

const { Option } = Select;
const { Title } = Typography;

const PurchaseForm = () => {
  const [form] = Form.useForm();
  const [brandForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [productForm] = Form.useForm();
  const [storeForm] = Form.useForm();
  const [taxForm] = Form.useForm();
  const [supplierForm] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsInPurchase, setProductsInPurchase] = useState([]);
  const [user, setUser] = useState(null);
  const [productModal, setProductModal] = useState(false);
  const [storeModal, setStoreModal] = useState(false);
  const [taxModal, setTaxModal] = useState(false);
  const [supplierModal, setSupplierModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [str, setStr] = useState(null);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [fields, setFields] = useState([]);
  const [barcode, setBarcode] = useState("");
  const [fileList, setFileList] = useState([]);
  const [image, setImage] = useState(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [brand, setBrand] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxAmount, setTaxAmount] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedTax, setSelectedTax] = useState();
  const [paidAmount, setPaidAmount] = useState("0.00");
  const [balanceAmount, setBalanceAmount] = useState("0.00");
  const [isPaidAmountEdited, setIsPaidAmountEdited] = useState(false);
  const navigate = useNavigate();
  const [purchaseDiscount, setPurchaseDiscount] = useState({
    value: 0,
    type: "percentage",
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [purchaseFormData, setPurchaseFormData] = useState(null);

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
  });

  const lineItemIdRef = useRef(0);

  const handleAddField = () => {
    setFields([...fields, { key: "", value: "" }]);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const handleInputChange = (index, fieldName, value) => {
    const updatedFields = [...fields];
    updatedFields[index][fieldName] = value;
    setFields(updatedFields);
  };

  const fetchUser = useCallback(() => {
    const userData = JSON.parse(localStorage?.getItem("userData"));
    setUser(userData);

    if (userData) {
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === selectedStoreId
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );

        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        }
      }
    }
  }, [selectedStoreId, productSettings]);

  const fetchBrand = async () => {
    try {
      const response = await API.get("/brands");
      setBrand(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch brand:", error);
      message.error(error.response?.data?.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await API.get("/category");
      setCategories(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      message.error("Unable to load categories.");
    }
  };

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    }
  };

  const fetchSuppliers = async (search = "") => {
    try {
      const params = { search };
      const response = await API.get("/suppliers/paginate", {
        params,
        page: 1,
        limit: 20,
      });
      setSuppliers(response?.data?.suppliers || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      message.error("Failed to fetch suppliers.");
    }
  };

  const fetchData = async () => {
    await Promise.all([fetchStores(), fetchProducts(), fetchSuppliers()]);
  };

  const fetchTax = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      setTaxes(taxResponse?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data.");
    }
  };

  const excludedProductIds = useMemo(() => {
    return productsInPurchase.map((item) => item.productId);
  }, [productsInPurchase]);

  const fetchProducts = async (search = "", excludedProductIds = []) => {
    try {
      const params = {
        search: search,
        // exclude: excludedProductIds,
      };
      const res = await API.get("/products/paginate", { params: params });
      setProducts(res?.data?.products);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data.");
    }
  };

  useEffect(() => {
    fetchData();
    fetchTax();
    fetchBrand();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchUser();
  }, [selectedStoreId]);

  useEffect(() => {
    fetchSuppliers();
  }, [selectedStoreId]);

  useEffect(() => {
    if (user && stores.length > 0) {
      const store = stores.find((store) => store?._id === user?.storeId);
      setStr(store);
      if (store) {
        form.setFieldsValue({ storeId: store?._id });
        setSelectedStoreId(store?._id);
      } else if (stores.length === 1) {
        form.setFieldsValue({ storeId: stores[0]?._id });
        setSelectedStoreId(stores[0]?._id);
      }
    }
  }, [user, stores, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    const { taxableAmount, totalAmount } = calculateSummary();

    // Check if status is completed and payment method is required
    if (values.status === "completed" && !paymentMethod) {
      message.error("Payment method is required for completed purchases.");
      setLoading(false);
      return;
    }

    // Validation: Ensure all products have a tax selected
    if (productsInPurchase.some((item) => !item.taxes)) {
      message.error("Please select a tax for all products in the purchase.");
      setLoading(false);
      return;
    }

    try {
      const purchaseData = {
        ...values,
        storeId: form.getFieldValue("storeId"),
        paidAmount: parseFloat(paidAmount).toFixed(2),
        paymentMethod: paymentMethod,
        balanceAmount: (totalAmount - parseFloat(paidAmount)).toFixed(2),
        products: productsInPurchase.map((item) => ({
          productId: item.productId,
          hsn_sac: item.hsn_sac,
          mrpPrice: parseFloat(item.mrpPrice).toFixed(2),
          quantity: Number(item.quantity),
          productDiscount: item.productDiscount,
          salePrice: parseFloat(item.salePrice).toFixed(2),
          totalPrice: parseFloat(item.totalPrice).toFixed(2),
          purchasePrice: parseFloat(item.purchasePrice).toFixed(2),
          tracking_number: item.trackingNo || "",
          batchNumber: item.batchNumber || "",
          gst: item.taxes
            ? { id: item.taxes._id, percentage: item.taxes.percentage }
            : { id: null, percentage: 0 },
          taxes: item.taxes
            ? [
                {
                  taxId: item.taxes._id,
                  taxName: item.taxes.taxName,
                  percentage: item.taxes.percentage,
                },
              ]
            : [],
        })),
        discount: purchaseDiscount,
        totalAmountWithoutTax: (totalAmount - taxableAmount).toFixed(2),
        totalAmount: totalAmount,
      };

      setPurchaseFormData(purchaseData);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Error preparing purchase data:", error);
      message.error("Failed to prepare purchase data.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!purchaseFormData) return;

    setLoading(true);
    try {
      const res = await API.post("/purchase", purchaseFormData);
      if (res.status === 200) {
        form.resetFields();
        message.success("Purchase created successfully!");
        setShowConfirmationModal(false);
        setPurchaseFormData(null);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      message.error(
        error?.response?.data?.message || "Failed to save purchase."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddBrand = async (newBrand) => {
    try {
      const response = await API.post("/brands", newBrand);
      message.success("Brand added successfully!");
      setBrandModalOpen(false);
      fetchBrand();
      brandForm.resetFields();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  const handleAddCategory = async (values) => {
    try {
      const response = await API.post(`/category`, values);
      if (response.status === 200) {
        message.success("Category added successfully!");
        setCategoryModalOpen(false);
        fetchCategories();
        categoryForm.resetFields();
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  const handleAddSupplier = async (values) => {
    try {
      const response = await API.post("/suppliers", values);
      if (response.status === 200) {
        message.success("Supplier added successfully!");
        setSupplierModal(false);
        fetchData();
        supplierForm.resetFields();
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  const handleAddStore = async (values) => {
    try {
      const res = await API.post("/stores", values);
      if (res.status === 200) {
        message.success("Store added successfully!");
        setStoreModal(false);
        fetchData();
        storeForm.resetFields();
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  const handleChange = async (info) => {
    let updatedFileList = [...info.fileList];
    updatedFileList = updatedFileList?.filter(
      (file) => file?.status !== "error"
    );

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      const imagePath = info?.file?.response?.imagePath;
      setImage(imagePath);
      setFileList([info?.file]);
    } else if (info.file.status === "error") {
      message.error(`${info?.file?.name} file upload failed.`);
    }
    setFileList(updatedFileList);
  };

  const customRequest = async (options) => {
    const { file, onSuccess, onError } = options;
    const userId = JSON.parse(localStorage.getItem("userData"))?.id;

    const formData = new FormData();
    formData.append("previousImagePath", image);
    formData.append("userId", userId);
    formData.append("image", file);

    try {
      const response = await API.post("/products/upload", formData);
      message.success("Image uploaded successfully!");
      setImage(response?.data?.imagePath);
      onSuccess(response?.data);
      setFileList([
        {
          uid: file?.uid,
          name: file?.name,
          status: "done",
          url: `http://localhost:5001/${response?.data?.imagePath}`,
        },
      ]);
    } catch (error) {
      console.error("Error uploading image:", error);
      onError(error);
      message.error("Failed to upload image.");
    }
  };

  const getCityState = async (pincode) => {
    const response = await fetch(
      `https://api.postalpincode.in/pincode/${pincode}`
    );
    const data = await response.json();

    if (data[0].Status === "Success") {
      const postOffice = data[0].PostOffice[0];
      supplierForm.setFieldsValue({
        contactInfo: {
          address: {
            city: postOffice?.District || "",
            state: postOffice?.State || "",
          },
        },
      });
    } else {
      supplierForm.setFieldsValue({
        contactInfo: {
          address: {
            city: null,
            state: null,
          },
        },
      });
      message.error("PinCode not found");
    }
  };

  const handlePaidAmountChange = (value) => {
    const newPaidAmount =
      value === null || value === "" ? "0.00" : parseFloat(value).toFixed(2);
    setPaidAmount(newPaidAmount);
    setIsPaidAmountEdited(true);

    const { totalAmount } = calculateSummary();
    setBalanceAmount(
      (parseFloat(totalAmount) - parseFloat(newPaidAmount)).toFixed(2)
    );
  };

  const handleRemoveImage = async (file) => {
    const productId = JSON.parse(localStorage.getItem("userData"))?.id;
    const fileUrl = file?.url;
    const fileName = fileUrl.split("/").pop();

    try {
      const response = await API.delete(`/products/image/${fileName}`, {
        data: { imagePath: file?.url },
      });

      if (response.status === 200) {
        message.success("Image removed successfully");
        setImage(null);
        setFileList([]);
      }
    } catch (error) {
      console.error("Error removing image:", error);
      message.error("Failed to remove image");
    }
  };

  const calculateTotalAmount = (products) =>
    products.reduce(
      (total, product) => total + parseFloat(product?.totalPrice || 0),
      0
    );

  const totalAmount = useMemo(
    () => calculateTotalAmount(productsInPurchase),
    [productsInPurchase]
  );

  useEffect(() => {
    const { totalAmount } = calculateSummary();
    form.setFieldsValue({ totalAmount: totalAmount });

    if (!isPaidAmountEdited) {
      setBalanceAmount(totalAmount);
    } else {
      setBalanceAmount(
        (parseFloat(totalAmount) - parseFloat(paidAmount)).toFixed(2)
      );
    }
  }, [totalAmount, paidAmount, form, isPaidAmountEdited]);

  const handleAddProduct = (product) => {
    if (!product) return;
    const trackingNo = form.getFieldValue("trackingNo") || "";
    const batchNumber = form.getFieldValue("batchNumber") || "";

    if (storeSettings.trackingNumber === "Yes" && !trackingNo.trim()) {
      message.error("Tracking Number is required when enabled.");
      return;
    }
    if (storeSettings.batchNumber === "Yes" && !batchNumber.trim()) {
      message.error("Batch Number is required when enabled.");
      return;
    }

    // Validate uniqueness for tracking number if enabled
    if (storeSettings.trackingNumber === "Yes" && trackingNo.trim()) {
      const existingTracking = productsInPurchase.find(
        (item) =>
          item.productId === product._id && item.trackingNo === trackingNo
      );
      if (existingTracking) {
        message.error("Tracking Number already present for this product.");
        return;
      }
    }

    // Validate uniqueness for batch number if enabled
    if (storeSettings.batchNumber === "Yes" && batchNumber.trim()) {
      const existingBatch = productsInPurchase.find(
        (item) =>
          item.productId === product._id && item.batchNumber === batchNumber
      );
      if (existingBatch) {
        message.error("Batch Number already present for this product.");
        return;
      }
    }

    setProductsInPurchase((prev) => {
      const updatedProducts = [
        ...prev,
        {
          id: lineItemIdRef.current++,
          productId: product?._id,
          productName: product?.specifications?.name,
          quantity: "1",
          totalPrice: parseFloat(product?.pricing?.purchasePrice || 0).toFixed(
            2
          ),
          hsn_sac: product?.hsn_sac || "",
          mrpPrice: parseFloat(product?.pricing?.mrpPrice || 0).toFixed(2),
          salePrice: parseFloat(product?.pricing?.salePrice || 0).toFixed(2),
          purchasePrice: parseFloat(
            product?.pricing?.purchasePrice || 0
          ).toFixed(2),
          trackingNo: trackingNo,
          batchNumber: batchNumber,
          taxes: null, // Tax is not set when adding product
          productDiscount: { value: 0, type: "percentage" },
        },
      ];
      fetchProducts(
        "",
        updatedProducts.map((item) => item.productId)
      );
      form.setFieldsValue({ trackingNo: "" });
      form.setFieldsValue({ batchNumber: "" });
      return updatedProducts;
    });
    setSelectedProduct(null);
  };

  const handleRemoveProduct = (id) => {
    setProductsInPurchase((prev) => {
      const updatedProducts = prev.filter((item) => item?.id !== id);
      fetchProducts(
        "",
        updatedProducts.map((item) => item.productId)
      );
      return updatedProducts;
    });
  };

  const handleQuantityChange = (id, quantity) => {
    if (
      quantity === null ||
      quantity < 0 ||
      !Number.isInteger(Number(quantity))
    ) {
      message.error(
        "Quantity must be a whole number greater than or equal to 0."
      );
      return;
    }
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: quantity,
              totalPrice: applyProductDiscount(
                item,
                parseFloat(quantity) * parseFloat(item.purchasePrice)
              ),
            }
          : item
      )
    );
  };

  const handlePurchasePriceChange = (id, newPurchasePrice) => {
    if (newPurchasePrice === null || newPurchasePrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              purchasePrice: parseFloat(newPurchasePrice).toFixed(2),
              totalPrice: applyProductDiscount(
                item,
                parseFloat(item.quantity) * parseFloat(newPurchasePrice)
              ),
            }
          : item
      )
    );
  };

  const handleMrpPriceChange = (id, newMrpPrice) => {
    if (newMrpPrice === null || newMrpPrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              mrpPrice: parseFloat(newMrpPrice).toFixed(2),
            }
          : item
      )
    );
  };

  const handleSalePriceChange = (id, newSalePrice) => {
    if (newSalePrice === null || newSalePrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              salePrice: parseFloat(newSalePrice).toFixed(2),
            }
          : item
      )
    );
  };

  const handleDiscountChange = (id, discountType, discountValue) => {
    if (discountValue === null || discountValue < 0) return;

    setProductsInPurchase((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const totalBasePrice =
            parseFloat(item.quantity) * parseFloat(item.purchasePrice);
          if (discountType === "percentage" && discountValue > 100) {
            message.error("Percentage discount cannot exceed 100%.");
            return item;
          }
          if (discountType === "flat" && discountValue > totalBasePrice) {
            message.error("Flat discount cannot exceed total price.");
            return item;
          }

          let totalPrice = applyProductDiscount(
            item,
            totalBasePrice,
            discountType,
            discountValue,
            item.taxes?.percentage || 0
          );

          return {
            ...item,
            totalPrice: totalPrice,
            productDiscount: {
              type: discountType,
              value: parseFloat(discountValue).toFixed(2),
            },
          };
        }
        return item;
      })
    );
  };

  const handleTrackingNoChange = (id, trackingNo) => {
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              trackingNo: trackingNo || "",
            }
          : item
      )
    );
  };

  const handleBatchNumberChange = (id, batchNumber) => {
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              batchNumber: batchNumber || "",
            }
          : item
      )
    );
  };

  const applyProductDiscount = (
    item,
    totalPrice,
    discountType = item.productDiscount?.type,
    discountValue = item.productDiscount?.value,
    taxPercentage = item.taxes?.percentage || 0 // Default to 0 if no tax
  ) => {
    if (discountType === "percentage" && discountValue) {
      totalPrice -= (parseFloat(discountValue) / 100) * totalPrice;
    } else if (discountType === "flat" && discountValue) {
      totalPrice -= parseFloat(discountValue);
    }
    totalPrice += (parseFloat(taxPercentage) / 100) * totalPrice;
    return parseFloat(totalPrice.toFixed(2));
  };

  const calculateSummary = () => {
    let netTotal = 0,
      totalDiscount = 0,
      taxableAmount = 0;

    productsInPurchase.forEach((item) => {
      const total = parseFloat(item.quantity) * parseFloat(item.purchasePrice);
      const discount =
        item.productDiscount?.type === "percentage"
          ? (parseFloat(item.productDiscount.value) / 100) * total
          : parseFloat(item.productDiscount?.value) || 0;

      const tax = item.taxes?.percentage
        ? ((total - discount) * parseFloat(item.taxes.percentage)) / 100
        : 0;

      taxableAmount += tax;
      netTotal += total;
      totalDiscount += discount;
    });

    const purchaseLevelDiscount =
      purchaseDiscount.type === "percentage"
        ? (parseFloat(purchaseDiscount.value) / 100) *
          (netTotal - totalDiscount)
        : parseFloat(purchaseDiscount.value) || 0;

    const totalAmount =
      netTotal - totalDiscount - purchaseLevelDiscount + taxableAmount;
    setTaxAmount(taxableAmount.toFixed(2));
    if (!isPaidAmountEdited) {
      setBalanceAmount(totalAmount.toFixed(2));
    } else {
      setBalanceAmount((totalAmount - parseFloat(paidAmount)).toFixed(2));
    }

    return {
      netTotal: netTotal.toFixed(2),
      totalDiscount: (totalDiscount + purchaseLevelDiscount).toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const formatNumber = (number) => {
    return `₹${new Intl.NumberFormat("en-IN").format(parseFloat(number))}`;
  };

  const columns = [
    {
      title: "Product Description",
      dataIndex: "productName",
      key: "productName",
    },
    ...(storeSettings.trackingNumber === "Yes"
      ? [
          {
            title: "Tracking No",
            dataIndex: "trackingNo",
            key: "trackingNo",
            render: (_, record) => (
              <Input
                value={record.trackingNo}
                onChange={(e) =>
                  handleTrackingNoChange(record.id, e.target.value)
                }
                placeholder="Enter Tracking No"
              />
            ),
          },
        ]
      : []),
    ...(storeSettings.batchNumber === "Yes"
      ? [
          {
            title: "Batch No",
            dataIndex: "batchNumber",
            key: "batchNumber",
            render: (_, record) => (
              <Input
                value={record.batchNumber}
                onChange={(e) =>
                  handleBatchNumberChange(record.id, e.target.value)
                }
                placeholder="Enter Batch No"
              />
            ),
          },
        ]
      : []),
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={1}
          value={record.quantity}
          onChange={(value) => handleQuantityChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => (
        <Space>
          <InputNumber
            addonAfter={
              <Select
                value={record.productDiscount?.type || "percentage"}
                onChange={(value) =>
                  handleDiscountChange(
                    record.id,
                    value,
                    record.productDiscount?.value || 0
                  )
                }
              >
                <Option value="percentage">%</Option>
                <Option value="flat">Flat</Option>
              </Select>
            }
            min={0}
            step={0.01}
            precision={2}
            placeholder="Discount"
            value={record.productDiscount?.value}
            onChange={(value) =>
              handleDiscountChange(
                record.id,
                record.productDiscount?.type || "percentage",
                value
              )
            }
            style={{ width: "150px" }}
          />
        </Space>
      ),
    },
    {
      title: "Purchase Price",
      dataIndex: "purchasePrice",
      key: "purchasePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.purchasePrice}
          onChange={(value) => handlePurchasePriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "MRP",
      dataIndex: "mrpPrice",
      key: "mrpPrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.mrpPrice}
          onChange={(value) => handleMrpPriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      key: "salePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.salePrice}
          onChange={(value) => handleSalePriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Tax</span>
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => setTaxModal(true)}
            style={{ marginLeft: "8px" }}
          />
        </div>
      ),
      key: "taxes",
      render: (_, record) => (
        <Select
          style={{ minWidth: 100 }}
          placeholder="Select Tax"
          value={record.taxes?._id || null}
          onChange={(value) => {
            const selectedTax = taxes.find((tax) => tax._id === value);

            setProductsInPurchase((prev) =>
              prev.map((item) =>
                item.id === record.id
                  ? {
                      ...item,
                      taxes: selectedTax || null,
                      totalPrice: applyProductDiscount(
                        item,
                        parseFloat(item.purchasePrice) *
                          parseFloat(item.quantity),
                        item.productDiscount?.type,
                        item.productDiscount?.value,
                        selectedTax?.percentage || 0
                      ),
                    }
                  : item
              )
            );
          }}
          status={!record.taxes ? "error" : ""}
        >
          {taxes.length === 0 ? (
            <Option disabled>No Tax available</Option>
          ) : (
            taxes.map((tax) => (
              <Option key={tax._id} value={tax._id}>
                {`${tax.taxName} (${tax.percentage}%)`}
              </Option>
            ))
          )}
        </Select>
      ),
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value) => formatNumber(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.id)}
        />
      ),
    },
  ];

  const handleAddTax = async (values) => {
    try {
      const res = await API.post("/taxes", values);
      if (res.status === 200) {
        message.success("Tax Added Successfully.");
        setTaxModal(false);
        fetchTax();
      }
    } catch (error) {
      message.error(error?.response?.data?.message);
    }
  };

  const handleAddProductModal = async (values) => {
    setLoading(true);
    try {
      const detailsObject = fields.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});
      const payload = {
        ...values,
        code: values?.code || "",
        specifications: { ...values?.specifications, details: detailsObject },
        pricing: {
          ...values?.pricing,
          taxes: selectedTax
            ? [
                {
                  taxId: selectedTax?._id,
                  taxName: selectedTax?.taxName,
                  percentage: selectedTax?.percentage,
                },
              ]
            : [],
          mrpPrice: parseFloat(values?.pricing?.mrpPrice || 0).toFixed(2),
          salePrice: parseFloat(values?.pricing?.salePrice || 0).toFixed(2),
          purchasePrice: parseFloat(
            values?.pricing?.purchasePrice || 0
          ).toFixed(2),
        },
        image: image,
      };
      const res = await API.post("/products", payload);
      if (res.status === 200) {
        message.success("Product added successfully!");
        productForm.resetFields();
        setFileList([]);
        setProductModal(false);
      }
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="storeId"
              label={
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Select Store</span>
                </div>
              }
            >
              <Select
                placeholder={
                  stores.length === 1 ? "Store (Auto-selected)" : "Select Store"
                }
                disabled={user?.role !== "admin" || stores.length === 1}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => setSelectedStoreId(value)}
              >
                {stores.length === 0 ? (
                  <Option disabled>No stores available</Option>
                ) : (
                  stores.map((store) => (
                    <Option key={store?._id} value={store?._id}>
                      {store?.name}
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="supplierId"
              label={
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Supplier</span>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    size="small"
                    style={{ marginLeft: 20 }}
                    onClick={() => setSupplierModal(true)}
                  />
                </div>
              }
              rules={[{ required: true, message: "Please select a supplier." }]}
            >
              <Select
                placeholder="Select Supplier"
                showSearch
                onSearch={(search) => fetchSuppliers(search, 1)}
                optionFilterProp="children"
                filterOption={false}
                onChange={(value) => form.setFieldsValue({ supplierId: value })}
                loading={isLoading}
              >
                {suppliers.map((supplier) => (
                  <Option key={supplier?._id} value={supplier?._id}>
                    {supplier?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="invoiceId"
              label="Invoice Number"
              rules={[
                { required: true, message: "Please enter the invoice number." },
              ]}
            >
              <Input placeholder="Enter Invoice Number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="orderDate"
              label="Invoice Date"
              rules={[
                { required: true, message: "Please select an invoice date." },
              ]}
            >
              <DatePicker placeholder="Select Date" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={10}>
            <Select
              placeholder="Select a Product"
              value={selectedProduct?._id}
              onChange={(value) => {
                const product = products.find((prod) => prod?._id === value);
                setSelectedProduct(product);
              }}
              onSearch={(input) => fetchProducts(input, excludedProductIds)}
              style={{ flex: 1, width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.props?.optionText
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {products.map((product) => {
                const optionText = `${product.specifications?.name || ""} (${
                  product?.code || "--"
                })`;
                return (
                  <Option
                    key={product._id}
                    value={product._id}
                    optionText={optionText}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{`${product.specifications?.name || ""} (${
                        product?.code || "--"
                      })`}</span>
                      <span>{`(${formatNumber(
                        product?.pricing?.salePrice
                      )})`}</span>
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Col>
          {storeSettings.trackingNumber === "Yes" ? (
            <Col xs={24} sm={6}>
              <Form.Item name="trackingNo">
                <Input placeholder="Enter Tracking Number" />
              </Form.Item>
            </Col>
          ) : null}
          {storeSettings.batchNumber === "Yes" ? (
            <Col xs={24} sm={6}>
              <Form.Item name="batchNumber">
                <Input placeholder="Enter Batch Number" />
              </Form.Item>
            </Col>
          ) : null}
          <Col xs={24} sm={4} style={{ display: "flex", gap: "10px" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleAddProduct(selectedProduct)}
              disabled={!selectedProduct}
              style={{ flex: 1, whiteSpace: "nowrap", maxWidth: 200 }}
            >
              Add
            </Button>
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              size="small"
              style={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setProductModal(true)}
            />
          </Col>
        </Row>
        <Table
          style={{ marginTop: "12px" }}
          columns={columns}
          dataSource={productsInPurchase}
          rowKey="id"
          pagination={false}
          footer={() => {
            const { taxableAmount, netTotal, totalDiscount, totalAmount } =
              calculateSummary();
            const netTotalNum = parseFloat(netTotal) || 0;

            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "16px",
                  borderTop: "2px solid #e8e8e8",
                  padding: "16px 0",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                }}
              >
                <Row gutter={[24, 16]} align="top">
                  <Col xs={24} lg={16}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            margin: "0 0 12px 0",
                            color: "#1890ff",
                            fontSize: "16px",
                            fontWeight: "600",
                            borderBottom: "1px solid #e8e8e8",
                            paddingBottom: "8px",
                          }}
                        >
                          Purchase Discount & Payment
                        </h4>
                        <Row gutter={[16, 16]} align="middle">
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Purchase Discount
                            </label>
                            <InputNumber
                              addonAfter={
                                <Select
                                  value={purchaseDiscount.type}
                                  onChange={(value) =>
                                    setPurchaseDiscount((prev) => ({
                                      ...prev,
                                      type: value,
                                    }))
                                  }
                                >
                                  <Option value="percentage">%</Option>
                                  <Option value="flat">Flat</Option>
                                </Select>
                              }
                              min={0}
                              step={0.01}
                              precision={2}
                              placeholder="Purchase Discount"
                              value={purchaseDiscount.value}
                              onChange={(value) => {
                                if (value === null || value < 0) return;
                                const type = purchaseDiscount.type;
                                if (type === "percentage" && value > 100) {
                                  message.error(
                                    "Percentage discount cannot exceed 100%."
                                  );
                                  return;
                                }
                                if (type === "flat" && value > netTotalNum) {
                                  message.error(
                                    "Flat discount cannot exceed the total amount."
                                  );
                                  return;
                                }
                                setPurchaseDiscount((prev) => ({
                                  ...prev,
                                  value: parseFloat(value).toFixed(2),
                                }));
                              }}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                              }}
                            />
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Paid Amount
                            </label>
                            <InputNumber
                              min={0}
                              step={0.01}
                              precision={2}
                              value={paidAmount}
                              onChange={handlePaidAmountChange}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                              }}
                            />
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Balance Amount
                            </label>
                            <InputNumber
                              value={balanceAmount}
                              readOnly
                              precision={2}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                                backgroundColor: "#f5f5f5",
                                color: "#8c8c8c",
                              }}
                            />
                          </Col>
                        </Row>
                        <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              name="status"
                              rules={[{ required: true }]}
                              label={
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#595959",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Status
                                </label>
                              }
                              initialValue={"completed"}
                            >
                              <Select placeholder="Select Status" showSearch>
                                <Option value="pending">Pending</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="cancelled">Cancelled</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={16}>
                            <Form.Item
                              name="paymentMethod"
                              rules={[{ required: true }]}
                              label={
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#595959",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Payment Method{" "}
                                  <span style={{ color: "#ff4d4f" }}>*</span>
                                </label>
                              }
                            >
                              <Select
                                required
                                placeholder="Payment Method"
                                value={paymentMethod}
                                onChange={(value) => setPaymentMethod(value)}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor:
                                    form.getFieldValue("status") ===
                                      "completed" && !paymentMethod
                                      ? "#ff4d4f"
                                      : "#d9d9d9",
                                }}
                                status={
                                  form.getFieldValue("status") ===
                                    "completed" && !paymentMethod
                                    ? "error"
                                    : ""
                                }
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter the payment Method!",
                                  },
                                ]}
                                options={[
                                  { value: "cash", label: "Cash" },
                                  { value: "upi", label: "UPI" },
                                  {
                                    value: "credit_card",
                                    label: "Credit Card",
                                  },
                                  { value: "debit_card", label: "Debit Card" },
                                  {
                                    value: "bank_transfer",
                                    label: "Bank Transfer",
                                  },
                                  { value: "other", label: "Other" },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} lg={8}>
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        height: "fit-content",
                        position: "sticky",
                        top: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Net Total
                          </span>
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#ff4d4f",
                              fontWeight: "600",
                            }}
                          >
                            {formatNumber(netTotal)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Discount
                          </span>
                          <span style={{ fontSize: "16px", color: "#595959" }}>
                            {formatNumber(totalDiscount)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Tax
                          </span>
                          <span style={{ fontSize: "16px", color: "#595959" }}>
                            {formatNumber(taxableAmount)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Payable (Before Tax)
                          </span>
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#52c41a",
                              fontWeight: "600",
                            }}
                          >
                            {formatNumber(
                              (netTotal - totalDiscount).toFixed(2)
                            )}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 0",
                            borderTop: "2px solid #1890ff",
                            marginTop: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#1890ff",
                              fontWeight: "600",
                            }}
                          >
                            Total Payable
                          </span>
                          <span
                            style={{
                              fontSize: "20px",
                              color: "#1890ff",
                              fontWeight: "700",
                            }}
                          >
                            {formatNumber(totalAmount)}
                          </span>
                        </div>
                      </div>
                      <Form.Item style={{ marginTop: "20px", marginBottom: 0 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isLoading}
                          block
                          style={{
                            height: "48px",
                            fontSize: "16px",
                            fontWeight: "600",
                            borderRadius: "8px",
                          }}
                        >
                          Create Purchase
                        </Button>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          }}
        />
      </Form>

      {supplierModal && (
        <Modal
          title="Add Supplier"
          open={supplierModal}
          onCancel={() => setSupplierModal(false)}
          footer={null}
          width={600}
        >
          <Form
            form={supplierForm}
            layout="vertical"
            onFinish={handleAddSupplier}
          >
            <Form.Item
              label="Supplier Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the supplier name!" },
              ]}
            >
              <Input placeholder="Supplier Name" />
            </Form.Item>
            <Form.Item
              label="Phone"
              name={["contactInfo", "phone"]}
              rules={[
                { required: true, message: "Please enter the phone number!" },
              ]}
            >
              <Input placeholder="Phone Number" />
            </Form.Item>
            <Form.Item
              label="Email"
              name={["contactInfo", "email"]}
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email address!",
                },
              ]}
            >
              <Input placeholder="Email Address" />
            </Form.Item>
            <Form.Item
              label="GST Number"
              name="gstNumber"
              rules={[
                {
                  pattern:
                    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/,
                  message: "Please enter a valid GST number!",
                },
              ]}
            >
              <Input placeholder="GST Number" />
            </Form.Item>
            <Form.Item
              label="PAN Number"
              name="panNumber"
              rules={[
                {
                  pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  message: "Please enter a valid PAN number!",
                },
              ]}
            >
              <Input placeholder="PAN Number" />
            </Form.Item>
            <Form.Item label="Address">
              <Form.Item
                label="Street"
                name={["contactInfo", "address", "street"]}
                style={{ marginBottom: 8 }}
              >
                <Input placeholder="Enter your street address" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    name={["contactInfo", "address", "city"]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      placeholder="City will autofill based on pincode"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="State"
                    name={["contactInfo", "address", "state"]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      placeholder="State will autofill based on pincode"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="PinCode"
                name={["contactInfo", "address", "pincode"]}
                rules={[
                  {
                    pattern: /^[0-9]{6}$/,
                    message: "Pincode must be a 6-digit number!",
                  },
                ]}
              >
                <Input
                  placeholder="Enter your pincode"
                  type="text"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{6}$/.test(value)) {
                      getCityState(value);
                    }
                  }}
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {storeModal && (
        <Modal
          title="Add Store"
          open={storeModal}
          onCancel={() => setStoreModal(false)}
          footer={null}
          width={600}
        >
          <Form form={storeForm} layout="vertical" onFinish={handleAddStore}>
            <Form.Item
              label="Store Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the store name!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Address"
              name="address"
              rules={[{ required: true, message: "Please enter the address!" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Phone"
              name="phone"
              rules={[
                { required: true, message: "Please enter the phone number!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
              >
                Add Store
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {taxModal && (
        <Modal
          title="Add Tax"
          open={taxModal}
          onCancel={() => setTaxModal(false)}
          footer={null}
          width={600}
        >
          <Form
            form={taxForm}
            layout="vertical"
            onFinish={handleAddTax}
            autoComplete="off"
          >
            <Form.Item
              label="Tax Name"
              name="taxName"
              rules={[
                { required: true, message: "Please enter the tax name." },
              ]}
            >
              <Input placeholder="Enter tax name" />
            </Form.Item>
            <Form.Item
              label="Value (in %)"
              name="percentage"
              rules={[
                { required: true, message: "Please enter a valid percentage." },
                {
                  type: "number",
                  min: 0,
                  max: 100,
                  message: "Percentage should be between 0 and 100.",
                },
              ]}
            >
              <InputNumber
                placeholder="Enter tax percentage"
                min={0}
                max={100}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Tax
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {productModal && (
        <Modal
          title="Add Product"
          open={productModal}
          onCancel={() => setProductModal(false)}
          footer={null}
          width={1200}
        >
          <Form
            layout="vertical"
            onFinish={handleAddProductModal}
            autoComplete="off"
            form={productForm}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Product Name"
                  name={["specifications", "name"]}
                  rules={[{ required: true, message: "Enter product name!" }]}
                >
                  <Input placeholder="Enter product name" />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  label="Brand"
                  name="brand"
                  rules={[
                    { required: true, message: "Please select a brand!" },
                  ]}
                >
                  <Select
                    placeholder="Select brand"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {brand.map((b) => (
                      <Option key={b._id} value={b._id}>
                        {b.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={1}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setBrandModalOpen(true)}
                  style={{ marginTop: 30 }}
                  block
                />
              </Col>
              <Col span={7}>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: "Select category!" }]}
                >
                  <Select
                    placeholder="Select category"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {categories.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={1}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCategoryModalOpen(true)}
                  style={{ marginTop: 30 }}
                  block
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="HSN ASC"
                  name="hsn_sac"
                  rules={[{ required: true, message: "Enter HSN ASC!" }]}
                >
                  <Input placeholder="Enter HSN ASC" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Code" name="code">
                  <Input placeholder="Enter product code" />
                </Form.Item>
              </Col>
            </Row>
            <Title level={4} style={{ marginTop: 24 }}>
              Pricing
            </Title>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label="MRP"
                  name={["pricing", "mrpPrice"]}
                  rules={[{ required: true, message: "Enter MRP!" }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="Enter MRP"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Sale Price"
                  name={["pricing", "salePrice"]}
                  rules={[{ required: true, message: "Enter Sale Price!" }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="Enter Sale Price"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Purchase Price"
                  name={["pricing", "purchasePrice"]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="Enter Purchase Price"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="GST"
                  name="gst"
                  rules={[{ required: true, message: "Select GST!" }]}
                >
                  <Select
                    placeholder="Select GST"
                    onChange={(value) => setSelectedTax(value)}
                  >
                    {taxes.map((tax) => (
                      <Option key={tax._id} value={tax._id}>
                        {tax.taxName} ({tax.percentage}%)
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <div
                style={{
                  marginTop: "30px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ padding: "20px" }}
                >
                  Add Product
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {brandModalOpen && (
        <Modal
          title="Add Brand"
          open={brandModalOpen}
          onCancel={() => setBrandModalOpen(false)}
          footer={null}
          width={600}
        >
          <Form
            layout="vertical"
            onFinish={handleAddBrand}
            autoComplete="off"
            form={brandForm}
          >
            <Form.Item
              label="Brand Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the brand name." },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Brand
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {categoryModalOpen && (
        <Modal
          title="Add Category"
          open={categoryModalOpen}
          onCancel={() => setCategoryModalOpen(false)}
          footer={null}
          width={600}
        >
          <Form
            layout="vertical"
            onFinish={handleAddCategory}
            autoComplete="off"
            form={categoryForm}
          >
            <Form.Item
              label="Category Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the category name." },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Category
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {showConfirmationModal && (
        <Modal
          title={
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#4a90e2",
              }}
            >
              <span style={{ fontSize: "16px" }}>CONFIRM PURCHASE</span> <hr />
            </div>
          }
          open={showConfirmationModal}
          onCancel={() => {
            setShowConfirmationModal(false);
            setPurchaseFormData(null);
          }}
          footer={null}
          style={{
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
          width={500}
        >
          <div style={{ padding: "24px", textAlign: "center" }}>
            <div
              style={{
                fontSize: "16px",
                color: "#595959",
                marginBottom: "24px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to create this purchase invoice?
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#8c8c8c",
                marginBottom: "24px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              This action will create a new purchase record and cannot be
              undone.
            </div>
            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setPurchaseFormData(null);
                }}
                style={{ minWidth: "100px" }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmPurchase}
                loading={isLoading}
                style={{ minWidth: "100px" }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PurchaseForm;
